import _ from 'underscore';
import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { ReactTemplate } from '@bingads-webui-react/react-template';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { notificationApi } from '@bingads-webui-react/notification-center';
import { PermissionsContext } from '@bingads-webui-react/hoc-with-permissions';
import { getWordpressInstructionsStyles } from '../styles/wordpress-instructions-styles';
import { CopyTag } from './copy-tag';

import imageStep2 from '../../media/wix-step2.png';
import imageStep3 from '../../media/wix-step3.png';
import imageStep4 from '../../media/wix-step4.png';
import imageStep5 from '../../media/wix-step5.png';
import imageStep6 from '../../media/wix-step6.png';
import imageStep7 from '../../media/wix-step7.png';
import imageStep8 from '../../media/wix-step8.png';
import imageStep9 from '../../media/wix-step9.png';

import { generateTag } from '../utils/tag-generator';

const WixInstructionsUnstyled = (props) => {
  const { classes, tag, logTrace } = props;
  const i18n = useContext(I18nContext);
  const noTranslateModel = { noTranslate: text => (<b>{text}</b>) };
  const onCopyCallback = ({ error, message, tagContent }) => {
    if (error) {
      logTrace(`[Wix] Copy error. Error: ${error} tagContent: ${JSON.stringify(tagContent)}`);
      notificationApi.showWarning(message);
    } else {
      logTrace('[Wix] Copy success.');
      notificationApi.showInfo(message);
    }
  };
  const permissions = useContext(PermissionsContext);
  const isUetSpaTrackingEnabled = _.result(permissions.dynamic, 'UetAutoSpaTracking');

  const tagContainerStyle = {
    container: {
      fontSize: '14px',
      lineHeight: '20px',
      color: '#323130',
      marginBottom: '12px',
      border: '1px solid black',
      padding: 20,
      wordWrap: 'break-word',
      whiteSpace: 'pre-wrap',
      width: 500,
    },
  };

  const containerStyle = _.extend({}, tagContainerStyle.container);
  return (
    <div className={classNames(classes.container, 'insert-instruction')}>
      <div className={classNames(classes.instructionContainer)}>
        <div className={classNames(classes.instructionTitle)}>
          <p>{i18n.getString(_TL_('Step 1: Copy the UET tag below.'))}</p>
        </div>
        <div className={classNames(classes.instructionBody)}>
          <CopyTag
            tag={tag}
            onCopyCallback={onCopyCallback}
            platformName="wix_ui"
          />
          <div style={containerStyle}>
            {generateTag(tag.type, tag.id, false, 'wix_ui', isUetSpaTrackingEnabled)}
          </div>
        </div>
      </div>
      <div className={classNames(classes.instructionContainer)}>
        <div className={classNames(classes.instructionTitle)}>
          <p>{i18n.getString(_TL_('Step 2: Log into your Wix Website dashboard and select Settings from the left menu. '))}</p>
        </div>
        <div className={classNames(classes.instructionBody)}>
          <div>
            <img className={classNames(classes.instructionImage)} src={imageStep2} alt={i18n.getString(_TL_('Wix settings page'))} />
          </div>
        </div>
      </div>
      <div className={classNames(classes.instructionContainer)}>
        <div className={classNames(classes.instructionTitle)}>
          <p>{i18n.getString(_TL_('Step 3: Scroll to the Advanced bar at the bottom of the page and select Custom code.'))}</p>
        </div>
        <div className={classNames(classes.instructionBody)}>
          <div>
            <img className={classNames(classes.instructionImage)} src={imageStep3} alt={i18n.getString(_TL_('Custom code tab in Wix settings page'))} />
          </div>
        </div>
      </div>
      <div className={classNames(classes.instructionContainer)}>
        <div className={classNames(classes.instructionTitle)}>
          <p>{i18n.getString(_TL_('Step 4: Select + Add Custom Code from the upper-right corner of the page.'))}</p>
        </div>
        <div className={classNames(classes.instructionBody)}>
          <div>
            <img className={classNames(classes.instructionImage)} src={imageStep4} alt={i18n.getString(_TL_('Wix settings page'))} />
          </div>
        </div>
      </div>
      <div className={classNames(classes.instructionContainer)}>
        <div className={classNames(classes.instructionTitle)}>
          <p>{i18n.getString(_TL_('Step 5: In the text box under Paste the code snippet here, paste the UET code that you copied in Step 1.'))}</p>
        </div>
        <div className={classNames(classes.instructionBody)}>
          <div>
            <img className={classNames(classes.instructionImage)} src={imageStep5} alt={i18n.getString(_TL_('Wix settings page'))} />
          </div>
        </div>
      </div>
      <div className={classNames(classes.instructionContainer)}>
        <div className={classNames(classes.instructionTitle)}>
          <p>{i18n.getString(_TL_('Step 6: Enter a name for your code in the Name box that you can easily identify later.'))}</p>
        </div>
        <div className={classNames(classes.instructionBody)}>
          <div>
            <img className={classNames(classes.instructionImage)} src={imageStep6} alt={i18n.getString(_TL_('Wix settings page'))} />
          </div>
        </div>
      </div>
      <div className={classNames(classes.instructionContainer)}>
        <div className={classNames(classes.instructionTitle)}>
          <p>{i18n.getString(_TL_('Step 7: Under Add Code to Pages, select an option.'))}</p>
        </div>
        <div className={classNames(classes.instructionBody)}>
          <div>
            <img className={classNames(classes.instructionImage)} src={imageStep7} alt={i18n.getString(_TL_('Wix settings page'))} />
          </div>
          <p>
            <ReactTemplate
              model={noTranslateModel}
              template={i18n.getString(_TL_('{{noTranslate}}All pages{{/noTranslate}} adds the code to every page of your website, including any new pages that you create in the future.'))}
            />
          </p>
          <p>
            <ReactTemplate
              model={noTranslateModel}
              template={i18n.getString(_TL_('1. Select {{noTranslate}}Load code once{{/noTranslate}} if you want the code to load once per visit to your website.'))}
            />
          </p>
          <p>
            <ReactTemplate
              model={noTranslateModel}
              template={i18n.getString(_TL_('2. (Recommended) Select {{noTranslate}}Load code on each new page{{/noTranslate}} if you want the code to load each time a page is visited. This option will minimize the possibility of error.'))}
            />
          </p>
          <p>
            <ReactTemplate
              model={noTranslateModel}
              template={i18n.getString(_TL_('3. {{noTranslate}}Choose specific pages{{/noTranslate}} adds the code to only the pages you choose. Use the dropdown menu to select the specific pages you want to add the code to.'))}
            />
          </p>
        </div>
      </div>

      <div className={classNames(classes.instructionContainer)}>
        <div className={classNames(classes.instructionTitle)}>
          <p>{i18n.getString(_TL_('Step 8: Under Place Code in, select the area of your website where you want to place the code (Head, Body - start, or Body - end).'))}</p>
        </div>
        <div className={classNames(classes.instructionBody)}>
          <div>
            <img className={classNames(classes.instructionImage)} src={imageStep8} alt={i18n.getString(_TL_('Wix settings page'))} />
          </div>
        </div>
      </div>

      <div className={classNames(classes.instructionContainer)}>
        <div className={classNames(classes.instructionTitle)}>
          <p>{i18n.getString(_TL_('Step 9: Select Apply.'))}</p>
        </div>
        <div className={classNames(classes.instructionBody)}>
          <div>
            <img className={classNames(classes.instructionImage)} src={imageStep9} alt={i18n.getString(_TL_('Wix settings page'))} />
          </div>
        </div>
      </div>
    </div>
  );
};

WixInstructionsUnstyled.propTypes = {
  classes: PropTypes.shape(_.mapObject(
    getWordpressInstructionsStyles(),
    _.constant(PropTypes.string.isRequired)
  )).isRequired,
  tag: PropTypes.shape({
    id: PropTypes.number.isRequired,
    type: PropTypes.oneOf(['Container', 'UET']).isRequired,
  }).isRequired,
  logTrace: PropTypes.func.isRequired,
};

export const WixInstructions = withDefaultStyles(WixInstructionsUnstyled, getWordpressInstructionsStyles);
